import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Trophy, Target, CheckCircle, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function AfterSchoolPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 transition">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-semibold">Back to Home</span>
          </Link>
        </div>
      </div>

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-white to-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white px-4 py-2 text-green-800 shadow-sm text-sm font-semibold mb-6">
                <span className="inline-block h-2 w-2 rounded-full bg-green-600"></span>
                After-School Soccer • San Jose
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                A Turnkey After-School Soccer Program
                <span className="block text-green-700 mt-2">For Your School</span>
              </h1>

              <p className="text-lg text-slate-700 leading-relaxed mb-8">
                Soccer Sphere partners with elementary and middle schools to run safe, organized, and fun
                after-school soccer on campus. We bring the coaches, curriculum, and equipment.
                Families register through our website. Your school simply provides access to the field and
                helps share the program with parents.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a
                  href="#partner"
                  className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition"
                >
                  Partner With Soccer Sphere
                </a>
                <a
                  href="mailto:info@soccersphere.net?subject=After-School%20Program%20Inquiry"
                  className="inline-flex items-center border border-gray-300 bg-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
                >
                  Contact Us
                </a>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="font-bold text-sm text-slate-900">On-Campus</div>
                  <div className="mt-1 text-sm text-slate-600">Right after school—easy pickup.</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="font-bold text-sm text-slate-900">Turnkey</div>
                  <div className="mt-1 text-sm text-slate-600">We coach, plan, and manage equipment.</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="font-bold text-sm text-slate-900">Safe + Structured</div>
                  <div className="mt-1 text-sm text-slate-600">Clear routines and age-appropriate training.</div>
                </div>
              </div>
            </div>

            {/* Video Section */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
              <div className="relative aspect-[16/10] w-full bg-slate-900 overflow-hidden">
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                >
                  {/* Placeholder - user will need to add their video file */}
                  <source src="/images/videos/afterschool-highlight.mp4" type="video/mp4" />
                </video>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10"></div>

                {/* Text Overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="text-white text-sm font-semibold tracking-wider uppercase">After-School Program</p>
                  <h3 className="text-white text-2xl md:text-3xl font-bold mt-1">
                    Organized. Safe. High-Energy.
                  </h3>
                </div>
              </div>

              <div className="p-5 border-t border-gray-200">
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <div className="font-bold text-xs">Group Management</div>
                    <div className="text-gray-600 text-xs mt-1">Stations, rotations, and clear coaching cues.</div>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <div className="font-bold text-xs">Skill Development</div>
                    <div className="text-gray-600 text-xs mt-1">Ball mastery, passing, finishing, games.</div>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <div className="font-bold text-xs">Positive Culture</div>
                    <div className="text-gray-600 text-xs mt-1">Confidence, effort, teamwork, respect.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Strip */}
        <section className="py-10 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-sm">
                <div className="font-bold text-sm text-slate-900">We Provide Coaches</div>
                <p className="mt-2 text-sm text-slate-600">
                  Trained, organized, and great with kids. Consistent curriculum and session plans.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-sm">
                <div className="font-bold text-sm text-slate-900">We Bring Equipment</div>
                <p className="mt-2 text-sm text-slate-600">
                  Cones, pinnies, goals, balls—everything needed for high-quality sessions.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-sm">
                <div className="font-bold text-sm text-slate-900">Families Register Online</div>
                <p className="mt-2 text-sm text-slate-600">
                  Registration and payments handled through our website (simple for your staff).
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-sm">
                <div className="font-bold text-sm text-slate-900">School Helps Promote</div>
                <p className="mt-2 text-sm text-slate-600">
                  Share flyers/emails to parents and allow field use. We do the rest.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-14 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">How It Works</h2>
            <p className="text-center text-slate-600 max-w-2xl mx-auto mb-10">
              A simple partnership that creates a great after-school option for families—without adding workload to your staff.
            </p>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-bold text-green-700 mb-2">Step 1</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">We Align on Logistics</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Days & times (e.g., Mon/Wed 3:15–4:15)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Grades served (elementary, middle, or both)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Field/blacktop location and pickup routine</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-bold text-green-700 mb-2">Step 2</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">We Launch Registration</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>We create a school-specific registration link</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Families register online through Soccer Sphere</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Your school shares the link with families</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-bold text-green-700 mb-2">Step 3</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">We Run The Program</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Warm-up → skills → small-sided games</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Structured stations for big groups</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Safe supervision + positive coaching</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Program Format */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 shadow-sm">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">What A Session Looks Like</h2>
                <p className="text-slate-700 leading-relaxed mb-6">
                  Our after-school sessions are designed to keep kids moving, learning, and having fun—while keeping the group organized.
                  We use clear routines and stations to manage larger numbers smoothly.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-white border border-gray-200 p-4">
                    <div className="font-bold text-sm text-slate-900">Warm-Up</div>
                    <p className="mt-1 text-sm text-slate-600">Movement + coordination + ball touches</p>
                  </div>
                  <div className="rounded-xl bg-white border border-gray-200 p-4">
                    <div className="font-bold text-sm text-slate-900">Skill Stations</div>
                    <p className="mt-1 text-sm text-slate-600">Dribbling, passing, finishing, 1v1s</p>
                  </div>
                  <div className="rounded-xl bg-white border border-gray-200 p-4">
                    <div className="font-bold text-sm text-slate-900">Small-Sided Games</div>
                    <p className="mt-1 text-sm text-slate-600">Lots of touches, decisions, and teamwork</p>
                  </div>
                  <div className="rounded-xl bg-white border border-gray-200 p-4">
                    <div className="font-bold text-sm text-slate-900">Wrap-Up</div>
                    <p className="mt-1 text-sm text-slate-600">Cool-down, highlights, pickup routine</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 shadow-sm">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Safety + Supervision</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We take supervision seriously. We'll align with your school's procedures and ensure a consistent and safe pickup routine.
                </p>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Clear check-in/check-out process (aligned with your campus policy)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Structured stations to keep groups organized and engaged</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Age-appropriate activities and progressive skill teaching</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Positive coaching: respect, teamwork, confidence</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Partner CTA */}
        <section id="partner" className="py-14 bg-slate-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Let's Bring This To Your School</h2>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              If you're an administrator or after-school coordinator, we'd love to share a sample schedule and a simple rollout plan.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:info@soccersphere.net?subject=After-School%20Soccer%20Partnership%20Inquiry"
                className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition"
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </a>
              <a
                href="tel:+16692045870"
                className="inline-flex items-center border border-gray-600 bg-transparent text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call: (669) 204-5870
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default AfterSchoolPage;
